<template>
	<div class="vendor-page">
		<!-- Tab Navigation Header -->
		<v-card flat class="vendor-tab-card">
			<v-card-text class="pa-3">
				<div class="vendor-header-tabs">
					<v-btn
						depressed
						class="mr-2"
						:class="activeTab === 1 ? 'active' : null"
						@click="switchToCompanyTab"
					>
						<v-icon>mdi-domain</v-icon> Company
					</v-btn>
					<v-btn depressed :class="activeTab === 2 ? 'active' : null" @click="switchToContactTab">
						<v-icon>mdi-account-search</v-icon> Contact
					</v-btn>
				</div>
			</v-card-text>
		</v-card>

		<!-- Company Tab -->
		<div v-if="activeTab === 1" class="vendor-table-container">
			<v-card class="vendor-toolbar-card" elevation="0">
				<v-card-text class="pa-3">
					<v-row align="center" dense>
						<v-col cols="12" sm="6" md="4">
							<v-text-field
								v-model="searchKeywords"
								outlined
								dense
								hide-details
								placeholder="Search vendors..."
								prepend-inner-icon="mdi-magnify"
								clearable
								class="vendor-search"
								@input="onSearchInput"
								@click:clear="onSearchClear"
							></v-text-field>
						</v-col>
						<v-spacer></v-spacer>
						<v-col cols="auto">
							<v-btn depressed rounded color="success" @click="$router.push('/relationships/vendor/new')">
								New Vendor
							</v-btn>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>

			<v-data-table
				:headers="vendorHeaders"
				:items="vendors"
				:options.sync="vendorTableOptions"
				:server-items-length="vendorTotalCount"
				:loading="$apollo.queries.vendors.loading"
				:footer-props="footerProps"
				class="vendor-data-table"
				@click:row="onVendorClick"
			>
				<template v-slot:loading>
					<v-skeleton-loader type="table-row@10"></v-skeleton-loader>
				</template>

				<template v-slot:no-data>
					<div class="text-center py-8">
						<v-icon size="64" color="grey lighten-1">mdi-domain</v-icon>
						<p class="mt-4 text-h6 grey--text">No vendors found</p>
					</div>
				</template>

				<template v-slot:item.companyName="{ item }">
					<span>{{ item.companyName }}</span>
				</template>

				<template v-slot:item.address="{ item }">
					<span>{{ renderAddress(item) }}</span>
				</template>

				<template v-slot:item.vendorId="{ item }">
					<span>{{ item.vendorId }}</span>
				</template>

				<template v-slot:item.accountNumber="{ item }">
					<span>{{ item.accountNumber }}</span>
				</template>

				<template v-slot:item.actions="{ item }">
					<v-btn text color="primary" @click.stop="getVendor(item)">
						View Details
						<v-icon style="font-size: 1.4rem">mdi-arrow-right</v-icon>
					</v-btn>
				</template>
			</v-data-table>
		</div>

		<!-- Contact Tab -->
		<div v-else class="vendor-table-container">
			<v-card class="vendor-toolbar-card" elevation="0">
				<v-card-text class="pa-3">
					<v-row align="center" dense>
						<v-col cols="12" sm="6" md="4">
							<v-text-field
								v-model="searchKeywords"
								outlined
								dense
								hide-details
								placeholder="Search contacts..."
								prepend-inner-icon="mdi-magnify"
								clearable
								class="vendor-search"
								@input="onSearchInput"
								@click:clear="onSearchClear"
							></v-text-field>
						</v-col>
						<v-col cols="auto">
							<v-checkbox
								v-model="searchForm.inactive"
								class="form-checkbox mt-0 pt-0"
								label="Show Inactive"
								color="gray"
								hide-details
							></v-checkbox>
						</v-col>
						<v-spacer></v-spacer>
						<v-col cols="auto">
							<v-btn depressed rounded color="success" @click="$router.push('/relationships/vendor/new')">
								New Vendor
							</v-btn>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>

			<v-data-table
				:headers="contactHeaders"
				:items="contacts"
				:options.sync="contactTableOptions"
				:server-items-length="contactTotalCount"
				:loading="$apollo.queries.contacts.loading"
				:footer-props="footerProps"
				class="vendor-data-table"
				@click:row="onContactClick"
			>
				<template v-slot:loading>
					<v-skeleton-loader type="table-row@10"></v-skeleton-loader>
				</template>

				<template v-slot:no-data>
					<div class="text-center py-8">
						<v-icon size="64" color="grey lighten-1">mdi-account-search</v-icon>
						<p class="mt-4 text-h6 grey--text">No contacts found</p>
					</div>
				</template>

				<template v-slot:item.firstName="{ item }">
					<span>{{ item.firstName }}</span>
				</template>

				<template v-slot:item.lastName="{ item }">
					<span>{{ item.lastName }}</span>
				</template>

				<template v-slot:item.cellPhoneNumber="{ item }">
					<span>{{ item.cellPhoneNumber }}</span>
				</template>

				<template v-slot:item.email="{ item }">
					<span>{{ item.email }}</span>
				</template>

				<template v-slot:item.vendorId="{ item }">
					<span>{{ item.vendorId }}</span>
				</template>

				<template v-slot:item.companyName="{ item }">
					<span>{{ item.vendor ? item.vendor.companyName : "" }}</span>
				</template>

				<template v-slot:item.actions="{ item }">
					<v-btn text color="primary" @click.stop="getVendor(item)">
						View Details
						<v-icon style="font-size: 1.4rem">mdi-arrow-right</v-icon>
					</v-btn>
				</template>
			</v-data-table>
		</div>
	</div>
</template>

<script>
import gql from "graphql-tag";

export default {
	name: "Vendor",
	props: {
		activeTab: {
			type: Number,
			default: 1,
		},
	},
	data() {
		return {
			searchKeywords: "",
			searchTimeout: null,
			searchForm: {
				inactive: false,
			},
			vendors: [],
			contacts: [],
			vendorTotalCount: 0,
			contactTotalCount: 0,
			vendorTableOptions: {
				page: 1,
				itemsPerPage: 15,
				sortBy: [],
				sortDesc: [],
			},
			contactTableOptions: {
				page: 1,
				itemsPerPage: 15,
				sortBy: [],
				sortDesc: [],
			},
			vendorHeaders: [
				{ text: "Company Name", value: "companyName", width: "28%" },
				{ text: "Address", value: "address", sortable: false, width: "38%" },
				{ text: "Vendor #", value: "vendorId", width: "14%", align: "center" },
				{ text: "TL Account #", value: "accountNumber", width: "12%", align: "center" },
				{ text: "", value: "actions", sortable: false, align: "end", width: "8%" },
			],
			contactHeaders: [
				{ text: "First Name", value: "firstName", width: "15%" },
				{ text: "Last Name", value: "lastName", width: "15%" },
				{ text: "Phone Number", value: "cellPhoneNumber", sortable: false, width: "14%" },
				{ text: "Email Address", value: "email", width: "18%" },
				{ text: "Vendor #", value: "vendorId", width: "10%", align: "center" },
				{ text: "Company Name", value: "companyName", sortable: false, width: "20%" },
				{ text: "", value: "actions", sortable: false, align: "end", width: "8%" },
			],
			footerProps: {
				"items-per-page-options": [10, 15, 25, 50],
			},
		};
	},
	apollo: {
		vendors: {
			query: gql`
				query SearchVendors($term: String!, $pageOffset: Int!, $take: Int!, $order: [VendorSortInput!]) {
					searchVendors(searchTerm: $term, skip: $pageOffset, take: $take, order: $order) {
						items {
							vendorId
							companyName
							accountNumber
							addresses {
								addressLine1
								addressLine2
								city
								state
								postalCode
								isPrimaryAddress
							}
						}
						totalCount
					}
				}
			`,
			variables() {
				const { page, itemsPerPage, sortBy, sortDesc } = this.vendorTableOptions;
				let order;
				if (sortBy.length > 0) {
					const direction = sortDesc[0] ? "DESC" : "ASC";
					order = [{ [sortBy[0]]: direction }];
				} else {
					// Default sort by vendorId when no sort specified
					order = [{ vendorId: "ASC" }];
				}
				return {
					term: this.searchKeywords?.trim() || "",
					pageOffset: (page - 1) * itemsPerPage,
					take: itemsPerPage,
					order,
				};
			},
			update(data) {
				this.vendorTotalCount = data.searchVendors.totalCount;

				// Auto-navigate if exactly one result is found from a search with exact match
				if (
					this.searchKeywords.trim() !== "" &&
					data.searchVendors.items.length === 1 &&
					(this.searchKeywords === data.searchVendors.items[0]?.companyName ||
						this.searchKeywords === String(data.searchVendors.items[0]?.vendorId) ||
						this.searchKeywords === data.searchVendors.items[0]?.accountNumber)
				) {
					this.$nextTick(() => {
						this.getVendor(data.searchVendors.items[0]);
					});
				}

				return data.searchVendors.items;
			},
			skip() {
				return this.activeTab !== 1;
			},
			notifyOnNetworkStatusChange: true,
			debounce: 400,
		},
		contacts: {
			query: gql`
				query SearchVendorContacts($term: String!, $pageOffset: Int!, $take: Int!, $showInactive: Boolean!, $order: [VendorContactSortInput!]) {
					searchVendorContacts(
						searchTerm: $term
						skip: $pageOffset
						take: $take
						where: { isActive: { eq: $showInactive } }
						order: $order
					) {
						items {
							vendorContactId
							firstName
							lastName
							cellPhoneNumber
							email
							vendorId
							vendor {
								companyName
							}
						}
						totalCount
					}
				}
			`,
			variables() {
				const { page, itemsPerPage, sortBy, sortDesc } = this.contactTableOptions;
				let order;
				if (sortBy.length > 0) {
					const direction = sortDesc[0] ? "DESC" : "ASC";
					order = [{ [sortBy[0]]: direction }];
				} else {
					// Default sort by vendorContactId when no sort specified
					order = [{ vendorContactId: "ASC" }];
				}
				return {
					term: this.searchKeywords?.trim() || "",
					pageOffset: (page - 1) * itemsPerPage,
					take: itemsPerPage,
					showInactive: !this.searchForm.inactive,
					order,
				};
			},
			update(data) {
				this.contactTotalCount = data.searchVendorContacts.totalCount;

				// Auto-navigate if exactly one result is found from a search with exact match
				if (
					this.searchKeywords.trim() !== "" &&
					data.searchVendorContacts.items.length === 1 &&
					(this.searchKeywords === data.searchVendorContacts.items[0]?.email ||
						this.searchKeywords === `${data.searchVendorContacts.items[0]?.lastName}` ||
						this.searchKeywords === data.searchVendorContacts.items[0]?.cellPhoneNumber)
				) {
					this.$nextTick(() => {
						this.getVendor(data.searchVendorContacts.items[0]);
					});
				}

				return data.searchVendorContacts.items;
			},
			skip() {
				return this.activeTab !== 2;
			},
			notifyOnNetworkStatusChange: true,
			debounce: 400,
		},
	},
	computed: {
		navigationDirection() {
			return this.$store.getters["globalState/navigationDirection"];
		},
	},
	watch: {
		navigationDirection: {
			handler(newDirection) {
				if (newDirection) {
					const savedState = this.$store.getters["globalState/vendorsState"];
					if (savedState) {
						this.searchKeywords = savedState.keywords;
						this.searchForm.inactive = savedState.showInactive;

						if (this.activeTab === 1) {
							this.vendorTableOptions.page = Math.floor(savedState.offset / this.vendorTableOptions.itemsPerPage) + 1;
							this.vendorTableOptions.sortBy = savedState.sortBy || [];
							this.vendorTableOptions.sortDesc = savedState.sortDesc || [];
						} else {
							this.contactTableOptions.page = Math.floor(savedState.offset / this.contactTableOptions.itemsPerPage) + 1;
							this.contactTableOptions.sortBy = savedState.sortBy || [];
							this.contactTableOptions.sortDesc = savedState.sortDesc || [];
						}

						this.$nextTick(() => {
							if (typeof savedState.scrollPosition === "number") {
								setTimeout(() => {
									window.scrollTo({
										top: savedState.scrollPosition,
										left: 0,
										behavior: "smooth",
									});
								}, 500);
							}
						});
					}
				}
			},
			immediate: true,
		},
		"searchForm.inactive"() {
			if (this.activeTab === 2) {
				this.$apollo.queries.contacts.refetch();
			}
		},
	},
	methods: {
		renderAddress(vendor) {
			if (vendor.addresses && vendor.addresses.length > 0) {
				const address = `${vendor.addresses[0].addressLine1}, ${
					vendor.addresses[0].addressLine2 ? vendor.addresses[0].addressLine2 : ""
				} ${vendor.addresses[0].city}, ${vendor.addresses[0].state} ${vendor.addresses[0].postalCode}`;
				return address;
			} else {
				return "";
			}
		},

		async getVendor(vendor) {
			this.$router.push(`/relationships/vendor/${vendor.vendorId}`);
		},

		onVendorClick(vendor) {
			this.getVendor(vendor);
		},

		onContactClick(contact) {
			this.getVendor(contact);
		},

		onSearchInput() {
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}

			this.searchTimeout = setTimeout(() => {
				if (this.activeTab === 1) {
					this.vendorTableOptions.page = 1;
				} else {
					this.contactTableOptions.page = 1;
				}
			}, 400);
		},

		onSearchClear() {
			this.searchKeywords = "";
			if (this.activeTab === 1) {
				this.vendorTableOptions.page = 1;
			} else {
				this.contactTableOptions.page = 1;
			}
		},

		switchToCompanyTab() {
			this.$router.push("/relationships/vendor");
			this.searchForm.inactive = false;
			this.$apollo.queries.vendors.refetch();
		},

		switchToContactTab() {
			this.$router.push("/relationships/vendor/contacts");
			this.$apollo.queries.contacts.refetch();
		},
	},
	beforeDestroy() {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
	},
	beforeRouteLeave(to, from, next) {
		const currentOptions = this.activeTab === 1 ? this.vendorTableOptions : this.contactTableOptions;
		const offset = (currentOptions.page - 1) * currentOptions.itemsPerPage;

		this.$store.dispatch("globalState/saveVendorsState", {
			activeTab: this.activeTab,
			offset: offset,
			keywords: this.searchKeywords,
			showInactive: this.searchForm.inactive,
			scrollPosition: window.scrollY,
			sortBy: currentOptions.sortBy,
			sortDesc: currentOptions.sortDesc,
		});
		next();
	},
};
</script>

<style lang="scss" scoped>
.vendor-page {
	height: calc(100vh - 88px);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.vendor-table-container {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	overflow: hidden;
}

.vendor-tab-card {
	border-radius: 4px 4px 0 0 !important;
	border-bottom: 1px solid $color-gray;
	flex-shrink: 0;

	.vendor-header-tabs {
		.v-btn {
			color: $color-black;
			background-color: $color-gray-2;
			text-transform: initial;

			&.active {
				color: $color-white;
				background-color: $color-tertiary;
			}

			::v-deep {
				i {
					font-size: 2rem;
					margin-right: 0.8rem;
				}
			}
		}
	}
}

.vendor-toolbar-card {
	border-radius: 0 !important;
	border-bottom: 1px solid $color-gray;
	flex-shrink: 0;
}

.vendor-search {
	::v-deep .v-input__slot {
		background-color: $color-gray-light-2 !important;
	}
}

.vendor-data-table {
	border-radius: 0 0 4px 4px !important;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	overflow: hidden;

	::v-deep .v-data-table__wrapper {
		flex: 1;
		overflow-y: auto;
		overflow-x: auto;

		table {
			min-width: 600px;
		}

		tbody tr {
			cursor: pointer;
			transition: background-color 0.2s;

			&:hover {
				background-color: $color-gray-light !important;
			}
		}
	}

	::v-deep .v-data-footer {
		flex-shrink: 0;
	}

	::v-deep > .v-data-table__wrapper > table > thead > tr > th {
		font-size: 1.4rem !important;
		font-weight: 500 !important;
		color: #000 !important;
		text-transform: uppercase;
		background-color: $color-gray-light-2 !important;
		white-space: nowrap;
	}

	::v-deep > .v-data-table__wrapper > table > tbody > tr > td {
		height: 60px !important;
		font-size: 1.6rem !important;
	}

	::v-deep .v-data-footer {
		font-size: 1.25rem !important;
	}

	::v-deep .v-data-footer__select .v-select__selections .v-select__selection--comma {
		font-size: 1.15rem !important;
	}
}

.form-checkbox {
	::v-deep .v-label {
		font-size: 1.4rem;
	}
}
</style>
